import { PartyConfetti } from './PartyConfetti';
import { AlbumEmailLabel } from './AlbumEmailLabel';
import { rememberContributorName } from './albumSignup';
import { GuestPhotos } from './GuestPhotos';
import { pendingGuestDraft, saveGuestDraft } from './guestDraft';
import './story.css';
import { flushSync } from 'react-dom';
import { StoryPlayer, type StoryController } from './StoryPlayer';
import { StoryEditor } from './StoryEditor';
import { storyApi } from './story-api';
import { emptyStory, type StoryDocument } from '../shared/story';
import { ContentGuide } from './ContentGuide';
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { Photo, PhotoPage, SortOrder, PhotoSection } from '../shared/photo';
import { api, UploadAccessError } from './api';
import { UPLOAD_BAN_MESSAGE } from '../shared/photo';
import { useUploadAccess } from './useUploadAccess';
import { useEventStatus } from './useEventStatus';
import { EventGate } from './EventGate';
import { AlbumRequest } from './AlbumRequest';

import { createIdempotencyKey } from './idempotency';
import { useDialogFocus } from './useDialogFocus';
import { ActionLabel } from './DialogLayout';
import { AlbumSpotlight, GuideExit } from './AlbumGuide';
import { ContributionButton } from './ContributionButton';
import { rememberGuide, useAlbumGuide } from './useAlbumGuide';
import { BackupPanel } from './BackupPanel';
import type { BackupStatus } from '../shared/backup';
import { usePhotoPicker } from './usePhotoPicker';
import { ProfileEditor } from './ProfileEditor';
import { PartyCamera } from './PartyCamera';
import { restorePhotoDraft, savePhotoDraft } from './photoDraft';
import type { EventProfile } from '../shared/profile';

type IconName =
  | 'menu'
  | 'camera'
  | 'pencil'
  | 'plus'
  | 'grid'
  | 'feed'
  | 'close'
  | 'left'
  | 'right'
  | 'image'
  | 'arrow'
  | 'lock'
  | 'refresh'
  | 'check'
  | 'expand'
  | 'more'
  | 'download'
  | 'trash';
function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    menu: <path d="M4 6h16M4 12h16M4 18h16" />,
    pencil: <path d="m15 5 4 4M4 20l5-1L20 8a2.8 2.8 0 0 0-4-4L5 15z" />,
    camera: (
      <>
        <path d="M8 6l2-3h4l2 3h4v14H4V6z" />
        <circle cx="12" cy="12" r="4" />
      </>
    ),
    plus: <path d="M12 4v16M4 12h16" />,
    grid: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    ),
    feed: (
      <>
        <rect x="4" y="3" width="16" height="12" rx="2" />
        <path d="M4 19h16M4 22h10" />
      </>
    ),
    close: <path d="m6 6 12 12M18 6 6 18" />,
    left: <path d="m15 18-6-6 6-6" />,
    right: <path d="m9 18 6-6-6-6" />,
    image: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <circle cx="8" cy="8" r="1.5" />
        <path d="m3 17 6-6 4 4 3-3 5 6" />
      </>
    ),
    arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
    lock: (
      <>
        <rect x="5" y="10" width="14" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 8a8 8 0 1 0 0 8M20 3v5h-5" />
      </>
    ),
    check: <path d="m5 12 4 4 10-10" />,
    expand: <path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5" />,
    more: (
      <>
        <circle cx="12" cy="5" r="1" />
        <circle cx="12" cy="12" r="1" />
        <circle cx="12" cy="19" r="1" />
      </>
    ),
    trash: <path d="M4 7h16M9 7V4h6v3m3 0-1 14H7L6 7m4 4v6m4-6v6" />,
    download: <path d="M12 3v12m-5-5 5 5 5-5M4 16v5h16v-5" />,
  };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}
function date(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}
function errorText(error: unknown) {
  return error instanceof Error ? error.message : 'Something went wrong. Please try again.';
}

function FamilyMenu({
  open,
  onToggle,
  onClose,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDialogFocus(open, ref, onClose);
  return (
    <div
      ref={ref}
      className={`family-menu${open ? ' is-open' : ''}`}
      role={open ? 'dialog' : undefined}
      aria-modal={open || undefined}
      aria-label={open ? 'Menu' : undefined}
    >
      <button
        className="family-menu-toggle"
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        aria-controls="family-controls"
        onClick={onToggle}
      >
        <Icon name="menu" />
      </button>
      {open && (
        <>
          <div className="family-menu-shade" onClick={onClose} aria-hidden="true" />
          <section id="family-controls" className="family-menu-card" aria-labelledby="family-controls-title">
            <p className="eyebrow">Settings</p>
            <h2 id="family-controls-title">Menu</h2>
            <div className="family-menu-actions">{children}</div>
          </section>
        </>
      )}
    </div>
  );
}
function Modal({
  label,
  onClose,
  children,
  className = '',
}: {
  label: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDialogFocus(true, ref, onClose);
  useEffect(() => {
    if (className !== 'review-modal' || !window.visualViewport) return;
    const viewport = window.visualViewport;
    const backdrop = ref.current?.parentElement;
    const update = () => {
      backdrop?.style.setProperty('--review-viewport-height', `${viewport.height}px`);
      backdrop?.style.setProperty('--review-viewport-top', `${viewport.offsetTop}px`);
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, [className]);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={'modal-card ' + className}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        ref={ref}
        tabIndex={-1}
      >
        <button className="icon-button modal-close" aria-label="Close" onClick={onClose}>
          <Icon name="close" />
        </button>
        {children}
      </div>
    </div>
  );
}
function PhotoImage({ photo, grid = false }: { photo: Photo; grid?: boolean }) {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const source = grid ? photo.assets.thumbnail : photo.assets.display;
  return failed ? (
    <span className="image-fallback">
      <Icon name="image" />
      <span>Photo couldn’t load</span>
      <span
        className="retry-image"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setAttempt(attempt + 1);
          setFailed(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setAttempt(attempt + 1);
            setFailed(false);
          }
        }}
      >
        Try image again
      </span>
    </span>
  ) : (
    <img
      src={source + (attempt ? '?retry=' + attempt : '')}
      alt={photo.caption || 'A moment from Lloyd’s celebration'}
      width={grid ? 640 : photo.width}
      height={grid ? 640 : photo.height}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}
function PhotoMenu({
  photo,
  admin,
  onDelete,
}: {
  photo: Photo;
  admin: boolean;
  onDelete: (photo: Photo, ban?: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const item = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    item.current?.focus({ preventScroll: true });
    const dismiss = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [open]);
  return (
    <div
      className="photo-menu"
      ref={ref}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          setOpen(false);
          trigger.current?.focus({ preventScroll: true });
        } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          setOpen(true);
          const items = Array.from(
            ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
          );
          const current = items.indexOf(document.activeElement as HTMLButtonElement);
          const next =
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? items.length - 1
                : (current + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
          items[next]?.focus({ preventScroll: true });
        }
      }}
    >
      <button
        className="icon-button photo-menu-trigger"
        aria-label={'Photo options: ' + (photo.caption || date(photo.uploadedAt))}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={'photo-menu-' + photo.id}
        ref={trigger}
        onClick={() => setOpen(!open)}
      >
        <Icon name="more" />
      </button>
      {open && (
        <div
          className="photo-menu-items"
          role="menu"
          aria-label="Photo options"
          id={'photo-menu-' + photo.id}
          onPointerDown={(event) => {
            // Safari may blur the focused item before dispatching its click.
            // Keep focus inside the menu so onBlur cannot unmount the action.
            if (event.button === 0) event.preventDefault();
          }}
        >
          <button
            role="menuitem"
            ref={item}
            onClick={() => {
              setOpen(false);
              trigger.current?.focus({ preventScroll: true });
              onDelete(photo);
            }}
          >
            <Icon name="trash" />
            Remove photo
          </button>
          {admin && photo.canBanUploader && (
            <button
              role="menuitem"
              onClick={() => {
                setOpen(false);
                trigger.current?.focus({ preventScroll: true });
                onDelete(photo, true);
              }}
            >
              <Icon name="lock" />
              Remove photo & ban uploads for 15 minutes
            </button>
          )}
        </div>
      )}
    </div>
  );
}
function Viewer({
  photos,
  index,
  onIndex,
  onClose,
  admin,
  onDelete,
}: {
  photos: Photo[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
  admin: boolean;
  onDelete: (photo: Photo) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const photo = photos[index];
  useDialogFocus(true, ref, onClose);
  const move = useCallback(
    (delta: number) => onIndex(Math.max(0, Math.min(photos.length - 1, index + delta))),
    [index, photos.length, onIndex],
  );
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        move(-1);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        move(1);
      }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [move]);
  useEffect(() => {
    [photos[index - 1], photos[index + 1]].forEach((p) => {
      if (p) new Image().src = p.assets.display;
    });
  }, [photos, index]);
  return (
    <div
      className="viewer"
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={'Photo ' + (index + 1) + ' of ' + photos.length}
    >
      <div className="viewer-top">
        <span className="wordmark">
          Lloyd’s <em>90th</em>
        </span>
        <span aria-live="polite">
          {index + 1} / {photos.length}
        </span>
        <button className="icon-button" aria-label="Close photo" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <div
        className="viewer-stage"
        onTouchStart={(e) => {
          touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }}
        onTouchEnd={(e) => {
          if (!touch.current) return;
          const dx = e.changedTouches[0].clientX - touch.current.x;
          const dy = e.changedTouches[0].clientY - touch.current.y;
          if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.3) move(dx > 0 ? -1 : 1);
          touch.current = null;
        }}
      >
        <PhotoImage key={photo.id} photo={photo} />
        <button
          className="viewer-prev icon-button"
          disabled={index === 0}
          onClick={() => move(-1)}
          aria-label="Previous photo"
        >
          <Icon name="left" />
        </button>
        <button
          className="viewer-next icon-button"
          disabled={index === photos.length - 1}
          onClick={() => move(1)}
          aria-label="Next photo"
        >
          <Icon name="right" />
        </button>
      </div>
      <div className="viewer-caption">
        {photo.contributorName && <p className="photo-contributor">Shared by {photo.contributorName}</p>}
        {photo.caption && <p>{photo.caption}</p>}
        <div>
          <time dateTime={photo.uploadedAt}>{date(photo.uploadedAt)}</time>
          {(admin || photo.canRemove) && (
            <button className="text-button" onClick={() => onDelete(photo)}>
              <Icon name="trash" />
              Remove photo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

type Panel =
  | 'guest-photos'
  | 'sharing-help'
  | 'pick'
  | 'camera'
  | 'review'
  | 'login'
  | 'trash'
  | 'purge'
  | 'backups'
  | 'delete'
  | 'profile'
  | 'download'
  | 'end-event'
  | 'story'
  | 'story-setup'
  | null;
type Draft = {
  file: File;
  url: string;
  caption: string;
  contributorName: string;
  section: PhotoSection;
  key: string;
};
export function App() {
  const [section, setSection] = useState<PhotoSection>(() => pendingGuestDraft() ?? 'celebration');
  const albumSwipe = useRef<{
    x: number;
    y: number;
    id: number;
    horizontal: boolean;
    section: PhotoSection;
  } | null>(null);
  const suppressSwipeClickUntil = useRef(0);
  const [profile, setProfile] = useState<EventProfile | null>(null);
  const [sort, setSort] = useState<SortOrder>('newest');
  const [page, setPage] = useState(1);
  const [view, setView] = useState<'feed' | 'grid'>('grid');
  const [gallery, setGallery] = useState<PhotoPage | null>(null);
  const galleryRef = useRef<PhotoPage | null>(null);
  const [pending, setPending] = useState<PhotoPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [offline, setOffline] = useState(!navigator.onLine);
  const [panel, setPanel] = useState<Panel>(() => pendingGuestDraft() ? 'guest-photos' : null);
  const [story, setStory] = useState<StoryDocument>({ settings: structuredClone(emptyStory), assets: [] });
  const [storyError, setStoryError] = useState('');
  const [storyDraft, setStoryDraft] = useState<StoryDocument | null>(null);
  const [previewStory, setPreviewStory] = useState<StoryDocument | null>(null);
  const storyController = useRef<StoryController | null>(null);
  useEffect(() => {
    let active = true;
    const refresh = () => {
      void storyApi
        .get()
        .then((value) => {
          if (active && value.settings && Array.isArray(value.assets)) {
            setStory(value);
            setStoryError('');
          }
        })
        .catch(() => {
          if (active) setStoryError('The story could not be loaded. Please refresh and try again.');
        });
    };
    refresh();
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(refresh, 60000);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const [familyMenuOpen, setFamilyMenuOpen] = useState(false);
  const [draft, setDraftState] = useState<Draft | null>(null);
  const [draftRecoveryError, setDraftRecoveryError] = useState('');
  const draftVersion = useRef(0);
  const setDraft = useCallback((next: Draft | null) => {
    const version = ++draftVersion.current;
    setDraftState(next);
    setDraftRecoveryError('');
    void savePhotoDraft(
      next && {
        file: next.file,
        caption: next.caption,
        contributorName: next.contributorName,
        section: next.section,
        key: next.key,
      },
    ).catch(() => {
      if (draftVersion.current === version && next)
        setDraftRecoveryError(
          'This browser couldn’t keep a recovery copy. Keep this page open until you publish your photo.',
        );
    });
  }, []);
  const [uploading, setUploading] = useState(false);
  const [guestPhotosPending, setGuestPhotosPending] = useState(false);
  const [confirmGuestDiscard, setConfirmGuestDiscard] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const [viewer, setViewer] = useState<number | null>(null);
  const [toast, setToast] = useState('');
  const [admin, setAdmin] = useState({ authenticated: false, csrfToken: null as string | null });
  const [password, setPassword] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);
  const [trash, setTrash] = useState<Photo[]>([]);
  const [trashSelection, setTrashSelection] = useState<string[]>([]);
  const [purgeTargets, setPurgeTargets] = useState<string[]>([]);
  const [backupStatus, setBackupStatus] = useState<BackupStatus | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Photo | null>(null);
  const [banUploader, setBanUploader] = useState(false);
  const [settings, setSettings] = useState({ pollSeconds: 3, maxUploadBytes: 25 * 1024 * 1024 });
  const uploadAccess = useUploadAccess(settings.pollSeconds);
  const { accept: acceptUploadAccess } = uploadAccess;
  const familyAdmin = admin.authenticated && !uploadAccess.familyBlocked;
  const event = useEventStatus(admin.authenticated, settings.pollSeconds);
  // Login confirms family access immediately; the event poll may still contain
  // the previous guest response until its next request completes.
  const eventLocked = event.status?.live !== true && !familyAdmin;
  const canContribute =
    !uploadAccess.blocked &&
    (event.status?.live === true ||
      (familyAdmin && (event.status?.phase === 'waiting' || event.status?.phase === 'paused')));
  const guideContent = useRef<HTMLDivElement>(null);
  const [guideSettled, setGuideSettled] = useState(false);
  const celebrationTab = useRef<HTMLButtonElement>(null);
  const memoriesTab = useRef<HTMLButtonElement>(null);
  const desktopContribution = useRef<HTMLButtonElement>(null);
  const mobileContribution = useRef<HTMLButtonElement>(null);
  const albumToolbar = useRef<HTMLDivElement>(null);
  const [testingGuide, setTestingGuide] = useState(false);
  const familyGuide = familyAdmin && testingGuide;
  const [showAlbumRequest, setShowAlbumRequest] = useState(() => {
    if (new URLSearchParams(window.location.search).get('album') === 'request') return true;
    return false;
  });
  function closeAlbumRequest() {
    setShowAlbumRequest(false);
    try { if (!localStorage.getItem('lloyds-email-signup-v1')) localStorage.setItem('lloyds-email-signup-v1', 'dismissed'); } catch { /* Optional preference. */ }
    const url = new URL(window.location.href); url.searchParams.delete('album'); url.searchParams.delete('setup');
    window.history.replaceState(null, '', url);
  }
  const guideEnabled =
    testingGuide &&
    Boolean(profile) &&
    !showAlbumRequest &&
    !eventLocked &&
    (event.status?.live === true || familyGuide) &&
    !panel &&
    viewer === null &&
    !draft &&
    !loadError &&
    !offline;
  const guide = useAlbumGuide(Boolean(guideEnabled), !loading, event.status?.live === true);
  const guideAction = guide.phase.endsWith('-content');
  useEffect(() => {
    if (loading || gallery?.items.length || guide.active || event.status?.allowAlbumDownloads) return;
    const empty = guideContent.current;
    const toolbar = albumToolbar.current;
    const cameraButton = mobileContribution.current;
    const copy = empty?.querySelector<HTMLElement>('.empty-copy');
    if (!empty || !toolbar || !cameraButton || !copy) return;
    let frame = 0;
    let offset = 0;
    const measure = () => {
      const camera = cameraButton.getBoundingClientRect();
      if (!camera.width || !camera.height) return;
      const toolbarBottom = toolbar.getBoundingClientRect().bottom + window.scrollY;
      const text = copy.getBoundingClientRect();
      const naturalCentre = text.top + window.scrollY + text.height / 2 - offset;
      const bottomScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      // Position only the text for the resting view at the bottom of the gallery.
      // A transform leaves the gallery's size and the surrounding document flow intact.
      const centre = (toolbarBottom + camera.top + bottomScroll) / 2;
      offset = Math.max(0, centre - naturalCentre);
      copy.style.transform = `translateY(${offset}px)`;
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(toolbar);
    observer.observe(cameraButton);
    schedule();
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      copy.style.removeProperty('transform');
    };
  }, [section, loading, gallery?.items.length, guide.active, event.status?.allowAlbumDownloads]);

  useEffect(() => {
    if (!guide.active) setTestingGuide(false);
  }, [guide.active]);
  const [changingEvent, setChangingEvent] = useState(false);
  const [eventError, setEventError] = useState('');
  const [changingContributions, setChangingContributions] = useState(false);
  const [uploadReceived, setUploadReceived] = useState(false);
  const requestId = useRef(0);
  const inFlight = useRef<number | null>(null);
  const {
    library,
    camera,
    cameraRecovery,
    message: pickerMessage,
    openLibrary,
    openCamera,
    recoverCamera,
    reset: resetPicker,
  } = usePhotoPicker(choosePhoto, {
    enabled: canContribute,
    onRecovery: (destination) => {
      setPanel(destination === 'celebration' ? 'camera' : 'pick');
    },
  });
  const album = useRef<HTMLElement>(null);

  useEffect(() => {
    // Unknown/loading status is not evidence that the event has closed.
    // In particular, preserve the pending camera handoff on Android reloads.
    if (!event.status || !eventLocked) return;
    setViewer(null);
    setPanel((current) => (current === 'login' ? current : null));
    resetPicker();
  }, [eventLocked, event.status, resetPicker]);

  async function toggleEvent() {
    if (!admin.csrfToken || !event.status || changingEvent) return;
    setChangingEvent(true);
    setEventError('');
    try {
      event.accept(await api.setEventLive(!event.status.live, admin.csrfToken));
    } catch (error) {
      setEventError(errorText(error));
    } finally {
      setChangingEvent(false);
    }
  }

  async function endEvent() {
    if (!admin.csrfToken || changingEvent) return;
    setChangingEvent(true);
    setEventError('');
    try {
      event.accept(await api.endEvent(admin.csrfToken));
      setPanel(null);
    } catch (error) {
      setEventError(errorText(error));
    } finally {
      setChangingEvent(false);
    }
  }

  useEffect(() => {
    let stopped = false;
    void restorePhotoDraft()
      .then((saved) => {
        if (stopped || draftVersion.current || !saved) return;
        let url = '';
        try {
          url = URL.createObjectURL(saved.file);
        } catch {
          /* Review can work without a preview. */
        }
        setDraftState({ ...saved, url });
        setPreviewFailed(!url);
        selectSection(saved.section);
        setPanel('review');
      })
      .catch(() => {
        if (!stopped)
          setDraftRecoveryError(
            'The saved photo couldn’t be restored. Please choose it from your phone again.',
          );
      });
    return () => {
      stopped = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!familyAdmin) {
      setBackupStatus(null);
      return;
    }
    let stopped = false;
    let fetching = false;
    const refresh = async () => {
      if (fetching || document.visibilityState === 'hidden') return;
      fetching = true;
      try {
        const next = await api.backupStatus();
        if (!stopped) setBackupStatus(next);
      } catch (error) {
        if (!stopped && error instanceof UploadAccessError) acceptUploadAccess(error.access);
        else if (!stopped)
          setBackupStatus({
            state: 'error',
            message: 'Backup status could not be checked. Please try again shortly.',
            lastSeenAt: null,
            lastSuccessAt: null,
            protectedThrough: null,
            folderUrl: null,
            freeBytes: null,
            copiedFiles: 0,
            totalFiles: 0,
          });
      } finally {
        fetching = false;
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [familyAdmin, acceptUploadAccess]);

  useEffect(() => {
    if (!uploadAccess.paused) return;
    resetPicker();
    setPanel((current) => (['camera', 'pick', 'review'].includes(current ?? '') ? null : current));
  }, [uploadAccess.paused, resetPicker]);

  useEffect(() => {
    if (!uploadAccess.banned) return;
    resetPicker();
    setDraft(null);
    setViewer(null);
    setPanel(null);
    setAdmin({ authenticated: false, csrfToken: null });
    setPassword('');
    setTrash([]);
    setDeleteTarget(null);
    setToast('');
  }, [uploadAccess.banned, resetPicker, setDraft]);

  useEffect(() => {
    let stopped = false;
    let fetching = false;
    async function refreshProfile() {
      if (fetching || document.visibilityState === 'hidden') return;
      fetching = true;
      try {
        const next = await api.profile();
        if (!stopped) setProfile(next);
      } catch {
        /* Keep the last portrait during an interrupted connection. */
      } finally {
        fetching = false;
      }
    }
    void refreshProfile();
    const timer = setInterval(() => void refreshProfile(), settings.pollSeconds * 1000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [settings.pollSeconds, panel]);

  const acceptGallery = useCallback((data: PhotoPage) => {
    galleryRef.current = data;
    setGallery(data);
    setPending(null);
  }, []);
  const load = useCallback(
    async (order: SortOrder, currentPage: number, quiet = false) => {
      if (eventLocked) {
        requestId.current++;
        inFlight.current = null;
        galleryRef.current = null;
        setGallery(null);
        setPending(null);
        return;
      }
      if (quiet && inFlight.current !== null) return;
      const id = ++requestId.current;
      inFlight.current = id;
      if (!quiet) setLoading(true);
      try {
        const data = await api.photos(order, currentPage, section);
        if (id !== requestId.current) return;
        const previous = galleryRef.current;
        const hasNew =
          previous &&
          data.revision !== previous.revision &&
          data.items.some((photo) => !previous.items.some((p) => p.id === photo.id));
        if (quiet && hasNew && window.scrollY > 450) setPending(data);
        else acceptGallery(data);
        setLoadError('');
      } catch (error) {
        if (id === requestId.current) setLoadError(errorText(error));
      } finally {
        if (inFlight.current === id) inFlight.current = null;
        if (id === requestId.current) setLoading(false);
      }
    },
    [acceptGallery, section, eventLocked],
  );

  useEffect(() => {
    void load(sort, page);
    return () => {
      requestId.current += 1;
    };
  }, [sort, page, load]);
  useEffect(() => {
    void api
      .config()
      .then(setSettings)
      .catch(() => undefined);
    void api
      .session()
      .then(setAdmin)
      .catch((error) => {
        if (error instanceof UploadAccessError) acceptUploadAccess(error.access);
      });
    const online = () => setOffline(!navigator.onLine);
    window.addEventListener('online', online);
    window.addEventListener('offline', online);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', online);
    };
  }, [acceptUploadAccess]);
  useEffect(() => {
    if (panel || viewer !== null || offline) {
      requestId.current += 1;
      inFlight.current = null;
      return;
    }
    const refresh = () => {
      if (document.visibilityState === 'visible') void load(sort, page, true);
    };
    if (!galleryRef.current) refresh();
    const timer = window.setInterval(refresh, settings.pollSeconds * 1000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('online', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [panel, viewer, offline, settings.pollSeconds, sort, page, load]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 6500);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    return () => {
      if (draft) URL.revokeObjectURL(draft.url);
    };
  }, [draft?.url]); // eslint-disable-line react-hooks/exhaustive-deps

  function openPanel(value: Panel) {
    setFamilyMenuOpen(false);
    if (
      uploadAccess.familyBlocked &&
      ['login', 'trash', 'purge', 'backups', 'profile', 'download', 'story-setup'].includes(value ?? '')
    )
      return;
    if (guide.active) guide.finish();
    setToast('');
    setActionError('');
    setPanel(value);
  }
  function closePanel() {
    if (uploading || busy) return;
    if (panel === 'guest-photos' && guestPhotosPending) { setConfirmGuestDiscard(true); return; }
    if (panel === 'guest-photos') void saveGuestDraft(section, '', []).catch(() => {});
    if (panel === 'purge') {
      setPurgeTargets([]);
      setActionError('');
      setPanel('trash');
      return;
    }
    resetPicker();
    if (panel === 'review') {
      setDraft(null);
      setUploadError('');
    }
    setPanel(null);
  }
  function selectSection(next: PhotoSection) {
    if (next === section) return;
    requestId.current += 1;
    inFlight.current = null;
    galleryRef.current = null;
    setGallery(null);
    setPending(null);
    setLoadError('');
    setLoading(true);
    setPage(1);
    setSort('newest');
    setViewer(null);
    setSection(next);
  }
  function contribute() {
    if (guideEnabled && guide.active) {
      if (guide.phase === 'memories-content') guide.next();
      else if (guide.phase === 'celebration-content') guide.finish();
      return;
    }
    if (!canContribute) return;
    if (draft) {
      openPanel('review');
      return;
    }
    openPanel('guest-photos');
  }
  function choosePhoto(file: File, destination: PhotoSection = 'memories') {
    if (!canContribute) return;
    selectSection(destination);
    let url = '';
    try {
      url = URL.createObjectURL(file);
    } catch {
      // An unavailable preview must not prevent the message/publication step.
    }
    setDraft({
      file,
      url,
      caption: '',
      contributorName: '',
      section: destination,
      key: createIdempotencyKey(),
    });
    setPreviewFailed(!url);
    setUploadError(
      file.size > settings.maxUploadBytes
        ? 'This photo is larger than ' +
            Math.round(settings.maxUploadBytes / 1024 / 1024) +
            ' MB. Please choose a smaller photo.'
        : '',
    );
    setPanel('review');
  }
  async function publish() {
    if (
      !canContribute ||
      !draft ||
      !draft.contributorName.trim() ||
      uploading ||
      draft.file.size > settings.maxUploadBytes
    )
      return;
    setUploading(true);
    setUploadReceived(false);
    setProgress(0);
    setUploadError('');
    try {
      await api.upload(
        draft.file,
        draft.caption,
        draft.key,
        setProgress,
        draft.contributorName,
        draft.section,
        () => setUploadReceived(true),
      );
      resetPicker();
      rememberGuide('submission');
      rememberContributorName(draft.contributorName);
      setDraft(null);
      setPanel(null);
      setSort('newest');
      setPage(1);
      await load('newest', 1);
      setToast(
        draft.section === 'celebration'
          ? 'Your photo is in The Celebration. Thank you!'
          : 'Your memory is in Memories with Lloyd. Thank you!',
      );
      album.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
    } catch (error) {
      setUploadError(errorText(error));
      if (error instanceof UploadAccessError) acceptUploadAccess(error.access);
    } finally {
      setUploading(false);
    }
  }
  async function login(event: FormEvent) {
    event.preventDefault();
    if (uploadAccess.familyBlocked) return;
    setBusy(true);
    setActionError('');
    try {
      setAdmin(await api.login(password));
      setPassword('');
      setPanel(null);
      setFamilyMenuOpen(true);
      setToast('Family controls unlocked.');
    } catch (error) {
      setActionError(errorText(error));
      if (error instanceof UploadAccessError) acceptUploadAccess(error.access);
    } finally {
      setBusy(false);
    }
  }
  async function openTrash() {
    if (!familyAdmin) return;
    setBusy(true);
    setTrash([]);
    setTrashSelection([]);
    setPurgeTargets([]);
    openPanel('trash');
    try {
      setTrash((await api.trash()).items);
    } catch (error) {
      setActionError(errorText(error));
      if (error instanceof UploadAccessError) acceptUploadAccess(error.access);
    } finally {
      setBusy(false);
    }
  }

  function confirmPurge(ids: string[]) {
    if (busy || !ids.length) return;
    setPurgeTargets([...ids]);
    openPanel('purge');
  }

  async function purgePhotos() {
    if (!familyAdmin || !admin.csrfToken || busy || !purgeTargets.length) return;
    setBusy(true);
    setActionError('');
    try {
      const { deletedIds } = await api.purge(purgeTargets, admin.csrfToken);
      setTrash((items) => items.filter((photo) => !deletedIds.includes(photo.id)));
      setTrashSelection((ids) => ids.filter((id) => !deletedIds.includes(id)));
      setPurgeTargets([]);
      setPanel('trash');
      setToast(`${deletedIds.length} ${deletedIds.length === 1 ? 'photo' : 'photos'} permanently deleted.`);
    } catch (error) {
      setActionError(errorText(error));
      if (error instanceof UploadAccessError) acceptUploadAccess(error.access);
    } finally {
      setBusy(false);
    }
  }

  async function toggleContributions() {
    if (!familyAdmin || !admin.csrfToken || changingContributions) return;
    setChangingContributions(true);
    try {
      const next = await api.setUploadsPaused(!uploadAccess.paused, admin.csrfToken);
      acceptUploadAccess(next);
      setToast(next.uploadsPaused ? 'Uploads are currently paused.' : 'Photos and uploads have resumed.');
    } catch (error) {
      if (error instanceof UploadAccessError) acceptUploadAccess(error.access);
      setToast(errorText(error));
    } finally {
      setChangingContributions(false);
    }
  }
  async function moderate(action: 'remove' | 'restore' | 'ban', photo: Photo) {
    const ownRemoval = action === 'remove' && !familyAdmin && photo.canRemove;
    if ((!familyAdmin && !ownRemoval) || (!admin.csrfToken && !ownRemoval) || busy) return;
    setBusy(true);
    setActionError('');
    try {
      if (action === 'remove' || action === 'ban') {
        if (ownRemoval) await api.removeOwn(photo.id);
        else if (action === 'ban') await api.ban(photo.id, admin.csrfToken!);
        else await api.remove(photo.id, admin.csrfToken!);
        setPanel(null);
        setDeleteTarget(null);
      } else {
        await api.restore(photo.id, admin.csrfToken!);
        setTrash((items) => items.filter((p) => p.id !== photo.id));
        setTrashSelection((ids) => ids.filter((id) => id !== photo.id));
      }
      await load(sort, page);
      setToast(
        action === 'ban'
          ? 'Photo removed. The uploader is banned from Camera, Upload and Family access for 15 minutes.'
          : action === 'remove'
            ? ownRemoval
              ? 'Your photo was removed from the album.'
              : 'Photo removed. You can restore it in family access.'
            : 'Photo restored to the album.',
      );
    } catch (error) {
      setActionError(errorText(error));
      if (error instanceof UploadAccessError) acceptUploadAccess(error.access);
    } finally {
      setBusy(false);
    }
  }
  function changePage(next: number) {
    setPage(next);
    album.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }
  const photos = gallery?.items ?? [];
  const count = gallery?.total ?? 0;
  const shownPage = gallery?.page ?? page;

  return (
    <>
      <div
        className={eventLocked ? 'event-app event-app-locked' : 'event-app'}
        inert={eventLocked}
        aria-hidden={eventLocked || undefined}
      >
        <a className="skip-link" href="#album">
          Skip to the album
        </a>
        <header className="topbar">
          <a href="#" className="wordmark" aria-label="Lloyd’s 90th, back to top" inert={familyMenuOpen}>
            Lloyd’s <em>90th</em>
          </a>
          <ContributionButton
            className={`top-add${familyMenuOpen || (guideEnabled && guide.active) ? ' menu-hidden' : ''}`}
            section={section}
            buttonRef={desktopContribution}
            onClick={contribute}
            disabled={!canContribute && !(guideEnabled && guideAction)}
            describedBy={
              uploadAccess.banned
                ? 'upload-ban'
                : guideEnabled && guideAction
                  ? 'album-guide-description'
                  : undefined
            }
          />
          <div className="header-actions">
            {event.status?.phase !== 'ended' && <button className="album-signup-notice" aria-label="Get the album by email" title="Get the album by email"
              onClick={() => { guide.finish(); setFamilyMenuOpen(false); setShowAlbumRequest(true); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m4 7 8 6 8-6" /></svg>
              <span className="album-signup-dot" />
            </button>}
          <FamilyMenu
            open={familyMenuOpen}
            onToggle={() => {
              guide.finish();
              setFamilyMenuOpen((open) => !open);
            }}
            onClose={() => setFamilyMenuOpen(false)}
          >
            <button className="button family-access-button" disabled={eventLocked} onClick={() => openPanel('sharing-help')}>Quick guide</button>
            {familyAdmin ? (
              <div className="family-controls">
                <section className="family-event-controls" aria-label="Collection controls">
                  <div>
                    <p className="eyebrow">Family controls</p>
                    <h2>
                      {event.status?.phase === 'ended'
                        ? 'Collection closed'
                        : event.status?.live
                          ? 'Album is open to guests'
                          : event.status?.phase === 'paused'
                            ? 'Album is hidden from guests'
                            : 'Album is not open yet'}
                    </h2>
                    <p>
                      {event.status?.live
                        ? 'Guests can browse both albums. To stop new uploads while keeping photos visible, use Pause photo uploads below.'
                        : event.status?.phase === 'ended'
                          ? 'Uploads and signups are closed, and guest browsing is hidden. Check email delivery below. Family members can still manage photos.'
                          : 'Guests cannot browse or upload photos. Show the album again when you’re ready; family members can still manage photos.'}
                    </p>
                  </div>
                  <div className="family-event-actions">
                    <button
                      className="button"
                      disabled={changingEvent || !event.status || event.status.phase === 'ended'}
                      onClick={() => void toggleEvent()}
                    >
                      {changingEvent
                        ? 'Updating…'
                        : event.status?.phase === 'ended'
                          ? 'Collection closed'
                          : event.status?.live
                            ? 'Hide album'
                            : event.status?.phase === 'paused'
                              ? 'Show album'
                              : 'Open album'}
                    </button>
                    {event.status?.phase !== 'ended' && (
                      <button
                        className="secondary-button danger-secondary"
                        disabled={changingEvent || !event.status}
                        onClick={() => {
                          setEventError('');
                          openPanel('end-event');
                        }}
                      >
                        Send finished album…
                      </button>
                    )}
                  </div>
                  <p className="family-request-count">
                    {event.status?.registeredCount ?? 0}{' '}
                    {event.status?.registeredCount === 1 ? 'album request' : 'album requests'}
                  </p>
                  {event.status?.phase === 'ended' && event.status.delivery && (
                    <p role="status">
                      {!event.status.registeredCount
                        ? 'No album requests were registered.'
                        : event.status.delivery.state === 'complete'
                          ? `${event.status.delivery.accepted} download emails sent.`
                          : event.status.delivery.message ||
                            'Preparing the album and sending download emails…'}
                    </p>
                  )}
                  {!event.status?.notificationsReady && (
                    <p>Email delivery needs to be connected before album requests can open.</p>
                  )}
                  {eventError && (
                    <p className="form-error" role="alert">
                      {eventError}
                    </p>
                  )}
                </section>
                <div className="family-settings" role="group" aria-label="Album settings">
                  <div className="family-setting">
                    <span className="family-setting-copy"><strong>Album email sign-ups</strong>
                    <small>Collecting until 28 October. This date does not close collection or send emails automatically. When ready, choose Send finished album to close collection and email everyone signed up.</small></span>
                  </div>
                  <button
                    className="family-setting contribution-toggle"
                    role="switch"
                    aria-label="Pause photo uploads"
                    aria-checked={uploadAccess.paused}
                    disabled={changingContributions}
                    onClick={() => void toggleContributions()}
                  >
                    <span className="family-setting-copy">
                      <strong>Pause photo uploads</strong>
                      <small>
                        {changingContributions
                          ? 'Updating…'
                          : uploadAccess.paused
                            ? 'New uploads are stopped for guests and family. Album visibility is unchanged.'
                            : 'Uploads are enabled. Guests can add photos while the album is open.'}
                      </small>
                    </span>
                    <span className="toggle-track" aria-hidden="true">
                      <span />
                    </span>
                  </button>
                </div>
                <div className="family-tools" role="group" aria-label="Album tools">
                  <button
                    className="family-tool"
                    aria-label="Set up Lloyd’s story"
                    onClick={() => {
                      setStoryDraft(null);
                      openPanel('story-setup');
                    }}
                  >
                    <Icon name="image" />
                    <span>
                      <strong>Lloyd’s story</strong>
                      <small>Edit the slideshow, video and music</small>
                    </span>
                  </button>
                  <button
                    className="family-tool"
                    aria-label="Download all"
                    disabled={!(gallery?.albumTotal ?? count)}
                    onClick={() => openPanel('download')}
                  >
                    <Icon name="download" />
                    <span>
                      <strong>Download all</strong>
                      <small>Download both albums; no emails sent</small>
                    </span>
                  </button>
                  <button
                    className="family-tool"
                    aria-label="Recently removed"
                    onClick={() => void openTrash()}
                  >
                    <Icon name="trash" />
                    <span>
                      <strong>Recently removed</strong>
                      <small>Restore removed photos or delete permanently</small>
                    </span>
                  </button>
                  <button className="family-tool" aria-label="Backups" onClick={() => openPanel('backups')}>
                    <Icon name="refresh" />
                    <span>
                      <strong>Backups</strong>
                      <small>
                        {backupStatus &&
                        ['error', 'stale', 'paused', 'not_connected'].includes(backupStatus.state)
                          ? 'Needs attention'
                          : 'Google Drive copies'}
                      </small>
                    </span>
                  </button>
                  <button
                    className="family-tool"
                    aria-label="Lock controls"
                    onClick={() => {
                      void api
                        .logout(admin.csrfToken!)
                        .then(() => {
                          setFamilyMenuOpen(false);
                          setAdmin({ authenticated: false, csrfToken: null });
                          setToast('Family controls locked.');
                        })
                        .catch((e) => setToast(errorText(e)));
                    }}
                  >
                    <Icon name="lock" />
                    <span>
                      <strong>Lock controls</strong>
                      <small>Sign out of family controls on this device</small>
                    </span>
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="button family-access-button"
                onClick={() => openPanel('login')}
                disabled={uploadAccess.familyBlocked}
                aria-describedby={uploadAccess.banned ? 'upload-ban' : undefined}
              >
                <Icon name="lock" />
                <span>Family access</span>
              </button>
            )}
          </FamilyMenu>
          </div>
        </header>
        <main
          className="page-shell"
          data-guiding={guideEnabled && guide.active}
          onTouchStart={(event) => {
            albumSwipe.current = null;
            const target = event.target as Element;
            if (
              event.touches.length !== 1 ||
              panel ||
              viewer ||
              familyMenuOpen ||
              !target.closest('.album-sections, .album-layout') ||
              target.closest('input, textarea, select, [contenteditable="true"]')
            )
              return;
            suppressSwipeClickUntil.current = 0;
            albumSwipe.current = {
              x: event.touches[0].clientX,
              y: event.touches[0].clientY,
              id: event.touches[0].identifier,
              horizontal: false,
              section,
            };
          }}
          onTouchMove={(event) => {
            const start = albumSwipe.current;
            if (!start) return;
            if (event.touches.length !== 1) {
              albumSwipe.current = null;
              return;
            }
            const dx = event.touches[0].clientX - start.x;
            const dy = event.touches[0].clientY - start.y;
            if (!start.horizontal && Math.max(Math.abs(dx), Math.abs(dy)) >= 12) {
              if (Math.abs(dx) > Math.abs(dy) * 1.5) start.horizontal = true;
              else albumSwipe.current = null;
            }
          }}
          onTouchCancel={() => {
            albumSwipe.current = null;
          }}
          onTouchEnd={(event) => {
            const start = albumSwipe.current;
            albumSwipe.current = null;
            if (
              !start ||
              event.touches.length ||
              !event.changedTouches.length ||
              event.changedTouches[0].identifier !== start.id ||
              section !== start.section
            )
              return;
            const dx = event.changedTouches[0].clientX - start.x;
            const dy = event.changedTouches[0].clientY - start.y;
            if (!start.horizontal) {
              const button = (event.target as Element).closest('.album-sections button');
              if (button && Math.max(Math.abs(dx), Math.abs(dy)) < 12) {
                const next = button === celebrationTab.current ? 'celebration' : 'memories';
                suppressSwipeClickUntil.current = Date.now() + 500;
                guide.select(next);
                selectSection(next);
              }
              return;
            }
            if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
            suppressSwipeClickUntil.current = Date.now() + 500;
            const next = dx > 0 ? 'memories' : 'celebration';
            if (next !== section) {
              guide.select(next);
              selectSection(next);
            }
          }}
          onClickCapture={(event) => {
            if (event.detail !== 0 && Date.now() < suppressSwipeClickUntil.current) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          {uploadAccess.banned && (
            <section className="upload-ban" id="upload-ban" aria-label="Photo upload restriction">
              <div role="alert">
                <strong>Photo access temporarily suspended</strong>
                <p>{UPLOAD_BAN_MESSAGE}</p>
              </div>
              <p className="ban-countdown" role="timer" aria-live="off">
                Time remaining: {Math.floor(uploadAccess.seconds / 60)}:
                {String(uploadAccess.seconds % 60).padStart(2, '0')}
              </p>
              {uploadAccess.seconds === 0 && <p>Checking that photo access can resume…</p>}
            </section>
          )}
          {uploadAccess.error && (
            <p className="inline-notice" role="status">
              {uploadAccess.error}
            </p>
          )}
          <section className="event-profile" aria-labelledby="event-name">
            <div className="cover">
              <img src="/assets/broncos-crest-cover-v1.webp" alt="" fetchPriority="high" />
              <span className="cover-edition">BIRTHDAY ALBUM</span>
            </div>
            <div className="profile-content">
              <div className="story-entry">
                <button
                  className="story-trigger"
                  aria-label="Open Lloyd’s story"
                  aria-haspopup="dialog"
                  onClick={() => {
                    rememberGuide('story');
                    setPreviewStory(null);
                    flushSync(() => openPanel('story'));
                    storyController.current?.start();
                  }}
                >
                  <span
                    className={'avatar' + (profile?.custom ? ' custom-portrait' : '')}
                    aria-busy={!profile}
                  >
                    <span className="story-portrait">
                      {profile && (
                        <img src={profile.imageUrl} alt="Lloyd’s profile photo" fetchPriority="high" />
                      )}
                    </span>
                  </span>
                  <span className="story-label">Lloyd’s story</span>
                </button>
                {familyAdmin && profile && (
                  <button
                    className="story-edit text-button"
                    aria-label="Edit profile photo"
                    aria-haspopup="dialog"
                    title="Edit photo"
                    onClick={() => openPanel('profile')}
                  >
                    <span className="story-edit-icon">
                      <Icon name="pencil" />
                    </span>
                    <span>Edit photo</span>
                  </button>
                )}

              </div>
              <div className="profile-copy">
                <h1 id="event-name">
                  Lloyd’s <em>90th.</em>
                </h1>
                <p>All the people. All the stories. All the love.</p>
              </div>
              <div className="profile-meta">
                <span>
                  <strong>{loading && !gallery ? '—' : count}</strong>{' '}
                  {section === 'celebration'
                    ? count === 1
                      ? 'party photo'
                      : 'party photos'
                    : count === 1
                      ? 'memory'
                      : 'memories'}{' '}
                  shared
                </span>
              </div>
            </div>
          </section>
          <section className="collection-invitation" data-effects-paused={panel !== null || showAlbumRequest || familyMenuOpen} aria-labelledby="collection-title">
            <div className="collection-heading"><p className="eyebrow">Share your photos</p><span><svg className="collection-calendar" viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="3" y="4.5" width="14" height="13" rx="2" fill="#FFFDFC" stroke="#BC5960" strokeWidth="1.3"/><path d="M5 4.5h10a2 2 0 0 1 2 2v2H3v-2a2 2 0 0 1 2-2Z" fill="#C8525B"/><path d="M6.5 2.5v4m7-4v4" stroke="#8E3943" strokeWidth="1.5" strokeLinecap="round"/><path d="M7 12h2m2 0h2M7 15h2" stroke="#665F66" strokeWidth="1.5" strokeLinecap="round"/></svg>Share by 28 October</span></div>
            <h2 id="collection-title" aria-label="The party goes on."><span aria-hidden="true">{'The party goes on.'.split(' ').map((word, wordIndex, words) => <span className="album-email-word" key={wordIndex}>{wordIndex > 0 && ' '}{[...word].map((letter, letterIndex) => <span className="collection-title-letter" key={letterIndex} style={{ animationDelay: `${600 + (words.slice(0, wordIndex).join('').length + letterIndex) * 32}ms` }}>{letter}</span>)}</span>)}</span></h2>
            <p>Share your party pictures and favourite moments with Lloyd. Browse the growing album below, or sign up for the finished collection.</p>
            <div className="collection-actions">
              <button className="button" disabled={!canContribute} onClick={() => { selectSection('celebration'); openPanel('guest-photos'); }}>Add party photos <span>Pictures from Lloyd’s 90th</span><span className="collection-emoji" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none"><g className="party-balloon party-balloon-rose"><path d="M16 29C16 25 9 24 11 19" stroke="#805367" strokeWidth="1.3" strokeLinecap="round"/><ellipse cx="11" cy="11" rx="6" ry="8" fill="#A54A63" stroke="#65354C" strokeWidth="1.3"/><path d="m11 19-2 2h4l-2-2Z" fill="#A54A63"/><path d="M8 8c0-1 1-2 2-2" stroke="#FFF9F3" strokeWidth="1.6" strokeLinecap="round"/></g><g className="party-balloon party-balloon-gold"><path d="M16 29C16 24 24 22 22 16" stroke="#805367" strokeWidth="1.3" strokeLinecap="round"/><ellipse cx="22" cy="9" rx="5.5" ry="7" fill="#E8AF49" stroke="#936021" strokeWidth="1.3"/><path d="m22 16-2 2h4l-2-2Z" fill="#E8AF49"/><path d="m20 6-1 2" stroke="#FFF9F3" strokeWidth="1.6" strokeLinecap="round"/></g><circle cx="16" cy="29" r=".9" fill="#805367"/></svg></span></button>
              <button className="secondary-button" disabled={!canContribute} onClick={() => { selectSection('memories'); openPanel('guest-photos'); }}>Add memories with Lloyd <span>Older photos and favourite moments</span><span className="collection-emoji" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none"><path d="M5 10h5l2-3h8l2 3h5v16H5V10Z" fill="#805367" stroke="#4D2336" strokeWidth="1.6" strokeLinejoin="round"/><circle cx="16" cy="17" r="6" fill="#FFF9F3"/><circle cx="16" cy="17" r="3.8" fill="#4D2336"/><circle cx="15" cy="16" r="1.2" fill="#C5AFBC"/><path d="M24 13h1" stroke="#F4CE83" strokeWidth="2" strokeLinecap="round"/></svg></span></button>
            </div>
            <button className="collection-email text-button" aria-label="Get the finished album by email" onClick={() => setShowAlbumRequest(true)}><AlbumEmailLabel /> <span className="collection-emoji" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none"><rect x="4" y="7" width="24" height="18" rx="3" fill="#FFF9F3" stroke="#65354C" strokeWidth="1.8"/><path d="m5 9 11 9L27 9M5 24l8-8m14 8-8-8" stroke="#65354C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></span></button>
          </section>
          <nav className="album-sections" aria-label="Album sections">
            <div className="album-segmented" data-section={section}>
              <button
                ref={celebrationTab}
                aria-pressed={section === 'celebration'}
                aria-describedby={
                  guideEnabled && guide.phase === 'celebration' ? 'album-guide-description' : undefined
                }
                onClick={() => {
                  guide.select('celebration');
                  selectSection('celebration');
                }}
              >
                <Icon name="camera" />
                The Celebration
              </button>
              <button
                ref={memoriesTab}
                aria-pressed={section === 'memories'}
                aria-describedby={
                  guideEnabled && guide.phase === 'memories' ? 'album-guide-description' : undefined
                }
                onClick={() => {
                  guide.select('memories');
                  selectSection('memories');
                }}
              >
                <Icon name="image" />
                Memories with Lloyd
              </button>
            </div>
          </nav>
          <div className="album-layout">
            <section
              className="album"
              id="album"
              ref={album}
              aria-label={section === 'celebration' ? 'The Celebration' : 'Memories with Lloyd'}
            >
              <div className="album-toolbar" ref={albumToolbar}>
                <div className="album-options">
                  <label>
                    <select
                      aria-label="Sort by"
                      value={sort}
                      onChange={(e) => {
                        setSort(e.target.value as SortOrder);
                        setPage(1);
                      }}
                    >
                      <option value="newest">Newest first</option>
                      <option value="oldest">Oldest first</option>
                    </select>
                  </label>
                  <div className="view-toggle" role="group" aria-label="Album view">
                    <button
                      aria-label="Feed view"
                      aria-pressed={view === 'feed'}
                      onClick={() => setView('feed')}
                    >
                      <Icon name="feed" />
                    </button>
                    <button
                      aria-label="Grid view"
                      aria-pressed={view === 'grid'}
                      onClick={() => setView('grid')}
                    >
                      <Icon name="grid" />
                    </button>
                  </div>
                </div>
              </div>
              {(offline || loadError) && (
                <div className="inline-notice" role="status">
                  <span>
                    {offline
                      ? 'You’re offline. Your selected photo will stay here until you reconnect.'
                      : loadError}
                  </span>
                  <button className="text-button" onClick={() => void load(sort, page)}>
                    <Icon name="refresh" />
                    Retry
                  </button>
                </div>
              )}
              {pending && (
                <button
                  className="new-photos"
                  onClick={() => {
                    acceptGallery(pending);
                    album.current?.scrollIntoView({ behavior: 'instant' });
                  }}
                >
                  <Icon name="refresh" />
                  New photos in this section · Show updates
                </button>
              )}
              <div className="album-content">
                {loading && !gallery && (
                  <div className={'photo-list ' + view} aria-label="Loading photos">
                    {[0, 1, 2].map((n) => (
                      <div key={n} className="skeleton" />
                    ))}
                  </div>
                )}
                {!loading && (!photos.length || (guideEnabled && guideAction)) && !loadError && (
                  <div
                    ref={guideContent}
                    tabIndex={guideEnabled && guideAction ? -1 : undefined}
                    data-guide-settled={guideSettled}
                    className={
                      'empty-state' +
                      (guideEnabled && guide.phase === `${section}-content` ? ' guide-empty-cue' : '')
                    }
                  >
                    <div className="empty-copy">
                      <h3>{section === 'celebration' ? 'Share your party photos.' : 'Share a memory with Lloyd.'}</h3>
                      <p>
                        {section === 'celebration'
                          ? guideEnabled && guideAction
                            ? 'Use the camera below for photos of the celebration. Tap it to finish the guide.'
                            : 'Choose party pictures from your phone. You can add several at once.'
                          : guideEnabled && guideAction
                            ? 'Use Add a memory below for older photos and special moments. Tap it to continue the guide.'
                            : 'Choose older photos from your phone and add a few words about the memories.'}
                      </p>
                    </div>
                  </div>
                )}
                {!!photos.length && (
                  <div className={'photo-list ' + view} aria-busy={loading}>
                    {photos.map((photo, index) => (
                      <article className="photo-post" key={photo.id}>
                        <div
                          className="photo-frame"
                          style={
                            view === 'feed'
                              ? { aspectRatio: String(photo.width / photo.height), maxHeight: '680px' }
                              : undefined
                          }
                        >
                          <PhotoImage key={photo.id + view} photo={photo} grid={view === 'grid'} />
                          <button
                            className="photo-open"
                            aria-label={'Open photo: ' + (photo.caption || date(photo.uploadedAt))}
                            onClick={(event) => {
                              // Safari does not focus buttons on pointer activation by default.
                              event.currentTarget.focus({ preventScroll: true });
                              setViewer(index);
                            }}
                          >
                            <span className="expand-badge">
                              <Icon name="expand" />
                            </span>
                          </button>
                        </div>
                        {(familyAdmin || photo.canRemove) && (
                          <PhotoMenu
                            photo={photo}
                            admin={familyAdmin}
                            onDelete={(target, ban = false) => {
                              setBanUploader(ban);
                              setDeleteTarget(target);
                              openPanel('delete');
                            }}
                          />
                        )}
                        {view === 'feed' ? (
                          <div className="post-caption">
                            {photo.contributorName && (
                              <p className="photo-contributor">Shared by {photo.contributorName}</p>
                            )}
                            {photo.caption && <p>{photo.caption}</p>}
                            <time dateTime={photo.uploadedAt}>{date(photo.uploadedAt)}</time>
                          </div>
                        ) : (
                          <div>
                            <p className="grid-caption">{photo.caption || date(photo.uploadedAt)}</p>
                            {photo.contributorName && (
                              <p className="photo-contributor">Shared by {photo.contributorName}</p>
                            )}
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </div>
              {gallery && gallery.totalPages > 1 && (
                <nav className="pagination" aria-label="Album pages">
                  <button
                    className="secondary-button"
                    disabled={loading || shownPage <= 1}
                    onClick={() => changePage(shownPage - 1)}
                  >
                    <Icon name="left" />
                    Previous
                  </button>
                  <span>
                    Page {shownPage} of {gallery.totalPages}
                  </span>
                  <button
                    className="secondary-button"
                    disabled={loading || shownPage >= gallery.totalPages}
                    onClick={() => changePage(shownPage + 1)}
                  >
                    Next
                    <Icon name="right" />
                  </button>
                </nav>
              )}

            </section>
          </div>

        </main>
        <div
          className={`mobile-bar${guideEnabled && guideAction ? ' guide-footer' : ''}`}
          data-guide-settled={guideEnabled && guideAction && guideSettled}
          data-guide-tab={guideEnabled && guide.active && !guideAction}
          role="group"
          aria-label="Photo contribution"
          inert={familyMenuOpen || panel !== null}
          data-hidden={familyMenuOpen || panel !== null}
        >
          <ContributionButton
            section={section}
            buttonRef={mobileContribution}
            onClick={contribute}
            disabled={!canContribute && !(guideEnabled && guideAction)}
            describedBy={
              uploadAccess.banned
                ? 'upload-ban'
                : guideEnabled && guideAction
                  ? 'album-guide-description'
                  : undefined
            }
          />
        </div>
        <input ref={library} id="library" type="file" accept="image/*" hidden disabled={!canContribute} />
        <input
          ref={camera}
          id="camera"
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          disabled={!canContribute}
        />
        <input
          ref={cameraRecovery}
          id="camera-recovery"
          type="file"
          accept="image/*"
          hidden
          disabled={!canContribute}
        />
        {panel === 'story-setup' && familyAdmin && (
          <Modal label="Set up Lloyd’s story" className="story-setup-modal" onClose={closePanel}>
            <StoryEditor
              csrf={admin.csrfToken!}
              initial={storyDraft}
              onBusy={setBusy}
              onSaved={(value) => {
                setStory(value);
                setStoryDraft(value);
              }}
              onPreview={(value) => {
                setStoryDraft(value);
                setPreviewStory(value);
                flushSync(() => setPanel('story'));
                storyController.current?.start();
              }}
            />
          </Modal>
        )}
        {panel === 'story' &&
          (previewStory || story.settings.visible ? (
            <StoryPlayer
              document={previewStory ?? story}
              controller={storyController}
              onClose={() => {
                if (previewStory) {
                  setPreviewStory(null);
                  setPanel('story-setup');
                } else closePanel();
              }}
            />
          ) : (
            <Modal label="Lloyd’s story" className="story-modal" onClose={closePanel}>
              <div className="story-heading">
                <p className="eyebrow">A lifetime of memories</p>
                <h2>Lloyd’s story.</h2>
              </div>
              <div className="story-coming-soon">
                <p>{storyError || 'Lloyd’s story is being prepared. Come back here to watch it with us.'}</p>
                {familyAdmin && (
                  <button
                    className="button"
                    onClick={() => {
                      setStoryDraft(null);
                      setPanel('story-setup');
                    }}
                  >
                    Set up the story
                  </button>
                )}
                <button className="text-button" onClick={closePanel}>
                  Back to the album
                </button>
              </div>
            </Modal>
          ))}
        {panel === 'sharing-help' && (
          <Modal label="Quick guide" className="sharing-help-modal" onClose={closePanel}>
            <h2>Help fill Lloyd’s album.</h2>
            <p>Share your photos by 28 October. Use Add party photos for pictures from the celebration, or Add memories for older photos with Lloyd.</p>
            <ol><li>Choose one or several photos from your phone.</li><li>Review them, add your name once, and write any optional captions.</li><li>Tap Share. Keep the page open until your photos are confirmed.</li></ol>
            <p>Browse both albums any time while we collect photos. Choose Get the finished album by email to receive the fuller collection once it’s ready.</p>
            <button className="button full-width" onClick={closePanel}>Got it</button>
          </Modal>
        )}
        {panel === 'guest-photos' && canContribute && (
          <Modal label={section === 'celebration' ? 'Add party photos' : 'Add memories with Lloyd'} className={`guest-upload-modal${section === 'celebration' && !guestPhotosPending && !confirmGuestDiscard ? ' party-confetti-modal' : ''}`} onClose={closePanel}>
            {section === 'celebration' && !guestPhotosPending && !confirmGuestDiscard && <PartyConfetti />}
            {confirmGuestDiscard && <div className="guest-discard" role="alert">
              <h2>Keep your selected photos?</h2>
              <p>Your remaining photos haven’t been shared yet.</p>
              <button className="button full-width" autoFocus onClick={() => setConfirmGuestDiscard(false)}>Keep editing</button>
              <button className="secondary-button full-width" onClick={() => {
                void saveGuestDraft(section, '', []).catch(() => {});
                setConfirmGuestDiscard(false); setPanel(null);
              }}>Discard unshared photos</button>
            </div>}
            <div hidden={confirmGuestDiscard}>
            <GuestPhotos section={section} maxBytes={settings.maxUploadBytes} onBusy={setBusy} onPending={setGuestPhotosPending}
              onViewPhotos={() => { setPanel(null); requestAnimationFrame(() => { const album = document.getElementById('album'); album?.scrollIntoView({ block: 'start', behavior: 'auto' }); }); }}
              onGetAlbum={() => { setPanel(null); setShowAlbumRequest(true); }}
              onComplete={() => { setSort('newest'); setPage(1); void load('newest', 1); }}
              onAccessError={(error) => acceptUploadAccess(error.access)} />
            </div>
          </Modal>
        )}
        {panel === 'camera' && canContribute && (
          <Modal label="Take a photo" className="camera-recovery-modal" onClose={closePanel}>
            <PartyCamera
              onOpen={() => {
                setPanel(null);
                openCamera();
              }}
              onRecover={recoverCamera}
              message={pickerMessage || draftRecoveryError}
            />
          </Modal>
        )}
        {panel === 'pick' && canContribute && (
          <Modal label="Upload a memory" onClose={closePanel}>
            <h2>A memory with Lloyd.</h2>
            <p>Choose an old photo or a favourite moment from your library.</p>
            <div className="source-options">
              <button onClick={openLibrary}>
                <span>
                  <Icon name="image" />
                </span>
                <div>
                  <strong>Choose from library</strong>
                  <small>Find a favourite on your phone</small>
                </div>
                <Icon name="right" />
              </button>
            </div>
            {pickerMessage && (
              <p className="fine-print" role="status">
                {pickerMessage}
              </p>
            )}
            <p className="fine-print">You’ll review your photo before it’s shared.</p>
          </Modal>
        )}
        {panel === 'review' && draft && canContribute && (
          <Modal label="Review your photo" className="review-modal" onClose={closePanel}>
            <div className="review-scroll">
              <div className="review-preview">
                {previewFailed ? (
                  <div className="image-fallback">
                    <Icon name="image" />
                    <span>Preview unavailable in this browser.</span>
                    <small>You can try publishing, or try another photo.</small>
                  </div>
                ) : (
                  <img src={draft.url} alt="Your selected photo" onError={() => setPreviewFailed(true)} />
                )}
              </div>
              <div className="review-content">
                <p className="eyebrow">
                  {draft.section === 'celebration'
                    ? 'Sharing to The Celebration'
                    : 'Sharing to Memories with Lloyd'}
                </p>
                <h2>A moment worth sharing.</h2>
                <label htmlFor="contributor-name">
                  Your name <span>Required</span>
                </label>
                <input
                  id="contributor-name"
                  required
                  aria-required="true"
                  type="text"
                  autoComplete="name"
                  maxLength={80}
                  value={draft.contributorName}
                  disabled={uploading}
                  placeholder="Who is sharing this memory?"
                  onChange={(event) => setDraft({ ...draft, contributorName: event.target.value })}
                />
                <label htmlFor="caption">
                  Birthday message <span>Optional</span>
                </label>
                <textarea
                  id="caption"
                  rows={2}
                  onInput={(event) => {
                    const field = event.currentTarget;
                    field.style.height = 'auto';
                    field.style.height = `${field.scrollHeight + 2}px`;
                    field.scrollIntoView({ block: 'nearest' });
                  }}
                  value={draft.caption}
                  disabled={uploading}
                  maxLength={480}
                  placeholder="A few words for Lloyd…"
                  onChange={(e) =>
                    setDraft({ ...draft, caption: Array.from(e.target.value).slice(0, 240).join('') })
                  }
                />
                <span className="character-count">{Array.from(draft.caption).length} / 240</span>
                {uploadError && (
                  <p className="form-error" role="alert">
                    {uploadError}
                  </p>
                )}
                {draftRecoveryError && (
                  <p className="fine-print" role="status">
                    {draftRecoveryError}
                  </p>
                )}
                {uploading && (
                  <div className="upload-progress" role="status">
                    <progress max={100} value={progress} />
                    <span>
                      {uploadReceived
                        ? 'Photo received. Saving your moment…'
                        : progress < 100
                          ? 'Uploading photo · ' + progress + '%'
                          : 'Finishing your upload…'}
                    </span>
                  </div>
                )}
                {uploading && (
                  <p className="fine-print">
                    {uploadReceived
                      ? 'Busy moments can take a little longer. Your photo is waiting to be saved.'
                      : 'Please keep this page open while your photo uploads.'}
                  </p>
                )}
              </div>
            </div>
            <div className="modal-actions review-actions">
              <button
                className="secondary-button"
                disabled={uploading}
                onClick={() => {
                  resetPicker();
                  setDraft(null);
                  setUploadError('');
                  setPanel(draft.section === 'celebration' ? null : 'pick');
                  if (draft.section === 'celebration') openCamera();
                }}
              >
                {draft.section === 'celebration' ? 'Retake photo' : 'Choose another'}
              </button>
              <button
                className="button"
                disabled={
                  uploading ||
                  offline ||
                  !draft.contributorName.trim() ||
                  draft.file.size > settings.maxUploadBytes
                }
                onClick={() => void publish()}
              >
                <ActionLabel
                  busy={uploading}
                  idle={
                    <>
                      {uploadError ? 'Retry publish' : 'Publish photo'}
                      <Icon name="arrow" />
                    </>
                  }
                  working="Publishing…"
                />
              </button>
            </div>
          </Modal>
        )}
        {panel === 'profile' && familyAdmin && profile && (
          <Modal label="Edit profile photo" onClose={closePanel}>
            <ProfileEditor
              profile={profile}
              csrf={admin.csrfToken!}
              maxBytes={settings.maxUploadBytes}
              busy={busy}
              onBusy={setBusy}
              onSaved={(next) => {
                setProfile(next);
                setPanel(null);
                setToast('Profile photo updated.');
              }}
            />
          </Modal>
        )}
        {panel === 'delete' && deleteTarget && (
          <Modal label="Remove photo" onClose={closePanel}>
            <p className="eyebrow">{familyAdmin ? 'Family controls' : 'Your photo'}</p>
            <h2>{banUploader ? 'Remove photo and ban uploads?' : 'Remove this photo?'}</h2>
            <p>
              {banUploader
                ? 'This photo will be removed and its uploader will lose access to Camera, Upload and Family access for 15 minutes. They will see a warning and countdown. Restoring the photo will not lift the ban.'
                : familyAdmin
                  ? 'It will leave the guest album. You can restore it from Recently removed.'
                  : 'This will remove your photo from the album. The family can restore it if needed.'}
            </p>
            {actionError && (
              <p className="form-error" role="alert">
                {actionError}
              </p>
            )}
            <div className="modal-actions">
              <button className="secondary-button" disabled={busy} onClick={closePanel}>
                Keep photo
              </button>
              <button
                className="button danger-button"
                disabled={busy}
                onClick={() => void moderate(banUploader ? 'ban' : 'remove', deleteTarget)}
              >
                <ActionLabel
                  busy={busy}
                  idle={banUploader ? 'Remove & ban for 15 minutes' : 'Remove photo'}
                  working="Removing…"
                />
              </button>
            </div>
          </Modal>
        )}
        {panel === 'end-event' && familyAdmin && (
          <Modal label="Send finished album" onClose={closePanel}>
            <p className="eyebrow">Family controls</p>
            <h2>Close collection and send the album?</h2>
            <p>
              This stops new uploads and signups and hides guest browsing. Once accepted uploads finish,
              everyone signed up will be emailed a ZIP download link. You cannot reopen collection from these controls. Existing photos are kept.
            </p>
            <p className="dialog-note">
              {event.status?.registeredCount ?? 0}{' '}
              {event.status?.registeredCount === 1 ? 'person has' : 'people have'} requested the album.
            </p>
            {eventError && (
              <p className="form-error" role="alert">
                {eventError}
              </p>
            )}
            <div className="modal-actions">
              <button className="secondary-button" disabled={changingEvent} onClick={closePanel}>
                Keep collecting
              </button>
              <button
                className="button danger-button"
                disabled={changingEvent}
                onClick={() => void endEvent()}
              >
                <ActionLabel busy={changingEvent} idle="Close collection & send emails" working="Closing collection…" />
              </button>
            </div>
          </Modal>
        )}
        {panel === 'download' && familyAdmin && (
          <Modal label="Download album" onClose={closePanel}>
            <p className="eyebrow">Family controls</p>
            <h2>Keep every memory.</h2>
            <p>
              Download photos from both The Celebration and Memories with Lloyd in their original quality,
              plus the names and messages, in one ZIP file. Recently removed photos are excluded.
            </p>
            <a className="button full-width" href="/api/admin/download" download>
              Download ZIP
            </a>
            <p className="fine-print">
              Large albums may take a while. Your browser will show the download progress.
            </p>
          </Modal>
        )}
        {panel === 'trash' && familyAdmin && (
          <Modal label="Recently removed photos" className="trash-modal" onClose={closePanel}>
            <p className="eyebrow">Family controls</p>
            <h2>Recently removed.</h2>
            <p>Restore photos to the album, or select photos to permanently delete.</p>
            {trash.length > 0 && (
              <div className="trash-controls">
                <p role="status">
                  {trashSelection.length} of {trash.length} selected
                </p>
                <div className="trash-actions">
                  <button
                    className="secondary-button"
                    disabled={busy}
                    onClick={() => setTrashSelection(trash.map((photo) => photo.id))}
                  >
                    Select all
                  </button>
                  <button
                    className="secondary-button"
                    disabled={busy || !trashSelection.length}
                    onClick={() => setTrashSelection([])}
                  >
                    Clear selection
                  </button>
                  <button
                    className="button"
                    disabled={busy || !trashSelection.length}
                    onClick={() => confirmPurge(trashSelection)}
                  >
                    Delete selected
                  </button>
                  <button
                    className="secondary-button"
                    disabled={busy}
                    onClick={() => confirmPurge(trash.map((photo) => photo.id))}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
            {actionError && (
              <p className="form-error" role="alert">
                {actionError}
              </p>
            )}
            {busy && <p role="status">Updating album…</p>}
            {!busy && !trash.length && !actionError && (
              <div className="small-empty">
                <Icon name="check" />
                <p>No removed photos.</p>
              </div>
            )}
            <div className="trash-list">
              {trash.map((photo) => (
                <div key={photo.id}>
                  <label className="trash-choice">
                    <input
                      type="checkbox"
                      aria-label={`Select ${photo.caption || 'Birthday photo'}`}
                      checked={trashSelection.includes(photo.id)}
                      disabled={busy}
                      onChange={(event) =>
                        setTrashSelection((ids) =>
                          event.target.checked ? [...ids, photo.id] : ids.filter((id) => id !== photo.id),
                        )
                      }
                    />
                    <span>
                      <strong>{photo.caption || 'Birthday photo'}</strong>
                      <small>{date(photo.uploadedAt)}</small>
                    </span>
                  </label>
                  <button
                    className="secondary-button"
                    disabled={busy}
                    onClick={() => void moderate('restore', photo)}
                  >
                    <Icon name="refresh" />
                    Restore
                  </button>
                </div>
              ))}
            </div>
          </Modal>
        )}
        {panel === 'backups' && familyAdmin && (
          <Modal label="Google Drive backups" onClose={closePanel}>
            <BackupPanel
              status={backupStatus}
              csrf={admin.csrfToken!}
              onAccessError={(error) => acceptUploadAccess(error.access)}
            />
          </Modal>
        )}
        {panel === 'purge' && familyAdmin && (
          <Modal label="Permanently delete photos" onClose={closePanel} key="purge">
            <p className="eyebrow">Family controls</p>
            <h2>
              Permanently delete {purgeTargets.length} {purgeTargets.length === 1 ? 'photo' : 'photos'}?
            </h2>
            <p>
              This cannot be undone. These photos cannot be restored from Recently Removed. Google Drive
              removes copies only after recorded website deletions are confirmed during a successful sync.
            </p>
            {actionError && (
              <p className="form-error" role="alert">
                {actionError}
              </p>
            )}
            <div className="modal-actions">
              <button className="secondary-button" disabled={busy} onClick={closePanel}>
                Cancel
              </button>
              <button className="button danger-button" disabled={busy} onClick={() => void purgePhotos()}>
                <ActionLabel busy={busy} idle="Permanently delete" working="Deleting…" />
              </button>
            </div>
          </Modal>
        )}
        {viewer !== null && photos[viewer] && (
          <Viewer
            photos={photos}
            index={viewer}
            onIndex={setViewer}
            onClose={() => setViewer(null)}
            admin={familyAdmin}
            onDelete={(photo) => {
              setBanUploader(false);
              setViewer(null);
              setDeleteTarget(photo);
              openPanel('delete');
            }}
          />
        )}
        <div className={'toast ' + (toast ? 'visible' : '')} role="status">
          {toast && (
            <>
              <Icon name="check" />
              <span>{toast}</span>
              <button className="icon-button" aria-label="Dismiss message" onClick={() => setToast('')}>
                <Icon name="close" />
              </button>
            </>
          )}
        </div>
      </div>
      {eventLocked && panel !== 'login' && !showAlbumRequest && (
        <EventGate
          phase={event.status?.phase}
          allowAlbumDownloads={event.status?.phase !== 'ended'}
          onRequestAlbum={() => setShowAlbumRequest(true)}
          checking={!event.status}
          error={event.error}
          familyBlocked={uploadAccess.familyBlocked}
          onFamily={() => openPanel('login')}
        />
      )}
      {showAlbumRequest && (
        <Modal label="Get the album by email" className="album-signup-modal" onClose={closeAlbumRequest}>
          {!event.status ? <p>Checking album requests…</p> : event.status.phase !== 'ended'
            ? <><AlbumRequest initiallyOpen /><button className="secondary-button" onClick={closeAlbumRequest}>Back to the photos</button></>
            : <p>The event has ended and album requests are now closed.</p>}
        </Modal>
      )}
      {panel === 'login' && !uploadAccess.familyBlocked && (
        <Modal label="Family access" onClose={closePanel} className="family-login-modal">
          <p className="eyebrow">For the family</p>
          <h2>Look after the album.</h2>
          <p>Start, pause or end the event and manage the album.</p>
          <form onSubmit={(e) => void login(e)}>
            <label htmlFor="password">Family password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {actionError && (
              <p className="form-error" role="alert">
                {actionError}
              </p>
            )}
            <button className="button full-width" disabled={busy}>
              <ActionLabel busy={busy} idle="Unlock controls" working="Unlocking…" />
              <Icon name="lock" />
            </button>
          </form>
        </Modal>
      )}
      {guideEnabled && guide.active && <GuideExit onSkip={guide.finish} />}
      {guideEnabled && guide.active && guideAction && (
        <ContentGuide
          key={guide.phase}
          target={guideContent}
          button={mobileContribution}
          onSettled={setGuideSettled}
          onSkip={guide.finish}
        />
      )}
      <AlbumSpotlight
        open={Boolean(guideEnabled && guide.active)}
        target={guideAction ? desktopContribution : guide.phase === 'memories' ? memoriesTab : celebrationTab}
        mobileTarget={guideAction ? mobileContribution : undefined}
        section={guide.phase.startsWith('memories') ? 'memories' : 'celebration'}
        action={guideAction}
        onSkip={guide.finish}
      />
    </>
  );
}
